import { useState, useRef, useEffect } from 'react';

export function useGridControls() {
  const [gridViewportSize, setGridViewportSize] = useState(() => {
    const saved = localStorage.getItem('battleGridViewportSize');
    return saved ? JSON.parse(saved) : { width: 800, height: 600 };
  });
  const [zoomLevel, setZoomLevel] = useState(() => {
    const saved = localStorage.getItem('battleGridZoom');
    return saved ? parseFloat(saved) : 1.0;
  });
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isResizing, setIsResizing] = useState(false);
  const [resizeEdge, setResizeEdge] = useState<string | null>(null);
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const gridContainerRef = useRef<HTMLDivElement>(null);

  // Persist viewport size
  useEffect(() => {
    localStorage.setItem('battleGridViewportSize', JSON.stringify(gridViewportSize));
  }, [gridViewportSize]);

  // Persist zoom level
  useEffect(() => {
    localStorage.setItem('battleGridZoom', zoomLevel.toString());
  }, [zoomLevel]);

  // Resize effect — attaches/detaches mousemove + mouseup listeners
  useEffect(() => {
    if (!isResizing) return;

    const handleResizeMove = (e: MouseEvent) => {
      const deltaX = e.clientX - resizeStart.x;
      const deltaY = e.clientY - resizeStart.y;

      let newWidth = resizeStart.width;
      let newHeight = resizeStart.height;

      if (resizeEdge?.includes('right')) newWidth = Math.max(400, resizeStart.width + deltaX);
      if (resizeEdge?.includes('left'))  newWidth = Math.max(400, resizeStart.width - deltaX);
      if (resizeEdge?.includes('bottom')) newHeight = Math.max(300, resizeStart.height + deltaY);
      if (resizeEdge?.includes('top'))   newHeight = Math.max(300, resizeStart.height - deltaY);

      setGridViewportSize({ width: newWidth, height: newHeight });
    };

    const handleResizeEnd = () => {
      setIsResizing(false);
      setResizeEdge(null);
    };

    window.addEventListener('mousemove', handleResizeMove);
    window.addEventListener('mouseup', handleResizeEnd);
    return () => {
      window.removeEventListener('mousemove', handleResizeMove);
      window.removeEventListener('mouseup', handleResizeEnd);
    };
  }, [isResizing, resizeEdge, resizeStart]);

  const TILE_SIZE = 80;

  const autoFit = (gridCols: number, gridRows: number) => {
    if (!gridContainerRef.current) return;
    const { clientWidth: w, clientHeight: h } = gridContainerRef.current;
    if (w === 0 || h === 0) return;
    const zoom = Math.max(0.3, Math.min(2.0, Math.min(w / (gridCols * TILE_SIZE), h / (gridRows * TILE_SIZE)) * 0.92));
    setZoomLevel(zoom);
    setPanOffset({ x: 0, y: 0 });
  };

  const handleZoomIn = () => setZoomLevel(prev => Math.min(prev + 0.1, 2.0));
  const handleZoomOut = () => setZoomLevel(prev => Math.max(prev - 0.1, 0.3));
  const handleResetZoom = (gridCols?: number, gridRows?: number) => {
    if (gridCols && gridRows) {
      autoFit(gridCols, gridRows);
    } else {
      setZoomLevel(1.0);
      setPanOffset({ x: 0, y: 0 });
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (e.target === gridContainerRef.current || (e.target as HTMLElement).classList.contains('battle-grid')) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - panOffset.x, y: e.clientY - panOffset.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging) {
      setPanOffset({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
    }
  };

  const handleMouseUp = () => setIsDragging(false);
  const handleMouseLeave = () => setIsDragging(false);

  const handleResizeStart = (e: React.MouseEvent, edge: string) => {
    e.stopPropagation();
    e.preventDefault();
    setIsResizing(true);
    setResizeEdge(edge);
    setResizeStart({ x: e.clientX, y: e.clientY, width: gridViewportSize.width, height: gridViewportSize.height });
  };

  return {
    gridViewportSize,
    zoomLevel,
    panOffset,
    setPanOffset,
    isDragging,
    isResizing,
    gridContainerRef,
    autoFit,
    handleZoomIn,
    handleZoomOut,
    handleResetZoom,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleMouseLeave,
    handleResizeStart,
  };
}
